'use client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuthStore, useStoryStore } from '@/store';
import { SlugRoutes } from '@/types/api/common';
import { QuestionDetailResponse } from '@/types/api/question';
import { usePathname, useRouter } from 'next/navigation';
import React, { useState } from 'react';
import RegisterDialog from './RegisterDialog';

interface QuestionCategoryProps {
  questionInfo: QuestionDetailResponse['data'];
  slugRoute?: SlugRoutes;
  hideAddStoryAction?: boolean;
}

export const QuestionCategory = ({
  questionInfo,
  slugRoute,
  hideAddStoryAction,
}: QuestionCategoryProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const setQuestionId = useStoryStore((state) => state.setQuestionId);
  const setQuestionTitle = useStoryStore((state) => state.setQuestionTitle);
  const setStoryId = useStoryStore((state) => state.setStoryId);

  const onHandleAddStory = (
    e:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.KeyboardEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    e.preventDefault();

    if (questionInfo.id) {
      setQuestionId(questionInfo.id);
      const questionTypeUrl = slugRoute || pathname.split('/')[1];
      setStoryId(null);
      setQuestionTitle(questionInfo.questionTitle);
      router.push(`/add-story/new?redirectTo=${questionTypeUrl}`);
    }
  };
  const { user } = useAuthStore();
  const [isRegisterDialogOpen, setIsRegisterDialogOpen] = useState(false);
  const handleShowRegisterDialog = (
    e:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.KeyboardEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    e.preventDefault();

    // Save question details to store before showing register dialog
    if (questionInfo.id) {
      setQuestionId(questionInfo.id);
      setQuestionTitle(questionInfo.questionTitle);
      setStoryId(null);
    }

    setIsRegisterDialogOpen(true);
  };

  let registerDialogTitle = 'You’ve Got Something Valuable to Share.';
  let registerDialogDescription =
    'Create your free account to ask questions, contribute your experience, and help others navigate change.';
  switch (questionInfo.questionType) {
    case 'IDEA_STARTER':
      registerDialogTitle = 'You’ve Got Something Valuable to Share.';
      registerDialogDescription =
        'Create an account to post your story, ask questions, and connect with a growing community of workplace changemakers.';
      break;
    case 'DISCUSSIONS':
      registerDialogTitle = 'Join the Discussion. Your Insight Matters.';
      registerDialogDescription =
        'Sign up to ask a question, share a story, or weigh in on workplace innovation and change. Be part of a growing community solving real challenges together.';
      break;
    default:
      registerDialogTitle =
        'Get more from IHQX — ask questions, share your story, and connect with others solving real workplace challenges.';
      registerDialogDescription =
        'Create an account to post your story, ask questions, and connect with a growing community of workplace changemakers.';
      break;
  }
  return (
    <div className="flex items-center justify-between">
      <div>
        {questionInfo?.category?.name && (
          <Badge variant="secondary" className="rounded-full">
            {questionInfo?.category?.name}
          </Badge>
        )}
      </div>
      {!hideAddStoryAction && (
        <div className="flex items-center gap-4">
          {!!questionInfo.storyCount && (
            <p className="text-sm">
              {questionInfo.storyCount === 1
                ? `${questionInfo.storyCount} Story`
                : `${questionInfo.storyCount} Stories`}
            </p>
          )}
          {user ? (
            <Button
              onClick={onHandleAddStory}
              className="font-bold uppercase"
              size="lg"
              type="button"
            >
              Add your story
            </Button>
          ) : (
            <div onClick={(e) => e.stopPropagation()}>
              <RegisterDialog
                open={isRegisterDialogOpen}
                onOpenChange={setIsRegisterDialogOpen}
                title={registerDialogTitle}
                description={registerDialogDescription}
                redirectToAddStory={true}
                questionTypeUrl={slugRoute || pathname.split('/')[1]}
              >
                <Button
                  className="font-bold uppercase"
                  onClick={handleShowRegisterDialog}
                >
                  Add your story
                </Button>
              </RegisterDialog>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
